import { forOwn, get, isArray, set, unset } from 'lodash';
import POP_LEVEL from 'src/lib/api/Populations.json';
import { handleCreateKostenPositions } from 'src/pages/public/programms/Details/hooks/kostenPositionsHelper';
import { getOrgaDateFuerAntrag } from 'src/utils/orgaDatenhelper';
import { withKostenkategorieEinsatzbereichFilter } from '../../utils/einsatzbereichHelper';
import {
	generateUniqueId,
	getFieldNamefromDataPath,
	isObjectEmpty,
	removeDataKeys,
	removeIdsFromArrayObjects,
	removeNullDeep,
} from '../../utils/helper';
import { APIS } from '../constants/apis';
import { ANTRAG_VERBUNDPARTNER, EMOB_LIS_STANDORTE_PATHS, KMU_PATHS, RUS_PATH } from '../constants/componentsPath';
import { ANTRAG_TYP, ANTRAGS_FORMULAREN, ANTRAGS_STATUS, ART_DES_ANTRAGS } from '../constants/constant';
import DeleteRecord from './DeleteRecord';
import GetRecord from './GetRecord';
import GetRecords from './GetRecords';
import { PostRecord } from './PostRecord';

// import schemas
import Schema from '../paths/antrag_FULL_Paths.json';
import FoerderProgrammSchema from '../paths/foerderprogramm_FULL_Paths.json';
import KostenPositionSchema from '../paths/kostenposition_FULL_Paths.json';
const P = Schema.attributes;
const FP_P = FoerderProgrammSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

function getAssoziiertePartnerData(application: any) {
	// const antragsformular = get(application, P.Allgemein_Antragsformular.path, '');
	const partnerKey = getFieldNamefromDataPath(P.Assoziierte_Partner.path);

	const assoziiertePartner = get(application, P.Assoziierte_Partner.path, []);

	// Laut Ticket HA-2623 muss NUR FÜR LÖWE keine Assoziierten Partner angelegt werden.
	// LAut Ticket HA-2630 soll wieder die Ass. Partner aus der Skizze übernommen werden.
	// Kann sein, dass TW wieder keine Übernahme will. Daher erstmal nur auskommentiert.
	// if (antragsformular === ANTRAGS_FORMULAREN.Loewe) {
	// 	if (isArray(assoziiertePartner) && assoziiertePartner.length > 0) {
	// 		return {
	// 			[partnerKey]: [],
	// 		};
	// 	} else {
	// 		return {
	// 			[partnerKey]: [],
	// 		};
	// 	}
	// }

	if (!isArray(assoziiertePartner)) return {};

	const loiDateiKey = getFieldNamefromDataPath(P.Assoziierte_Partner.schema.attributes.LOI_Datei.path);
	const loiVorhandenKey = getFieldNamefromDataPath(P.Assoziierte_Partner.schema.attributes.LOI_Vorhanden.path);

	const updatedPartner = removeIdsFromArrayObjects(
		assoziiertePartner.map((partner: any) => {
			const loiDateiId = get(partner, 'LOI_Datei.id', null);
			const isLoiVorhanden = Boolean(loiDateiId || get(partner, loiVorhandenKey, false));
			return {
				...partner,
				[loiDateiKey]: null,
				...(isLoiVorhanden ? { [loiVorhandenKey]: true } : {}),
			};
		}),
	);

	return {
		[partnerKey]: updatedPartner,
	};
}

/**
 * Dupliziert den Antrag (ohne Kosten, usw.)
 * @param antragSkizzeId | ID vom Antrag oder Skizze, der dupliziert werden. (Prinzipiell wird immer Skizze zu Antrag dupliziert)
 * @param antragsFormular | AntragsFormular_Typ
 * @param currentAntragsTyp | Aktueller Antrags Typ (Skizze oder Antrag) | Wenn Skizze, dann wird Antrag erstellt, wenn Antrag, dann wird ein neuer Antrag (Duplikat) erstellt
 * @param foerderProgramm | Gesamten Förderprogramm Object (wird für die Einzureichende Anlagen und Erklärungen benötigt)
 * @returns | Object vom duplizierter Antrag
 */
async function DuplicateAntrag(
	antragSkizzeId: number,
	antragsFormular: string,
	currentAntragsTyp: string = ANTRAG_TYP.Skizze,
	foerderProgramm?: any,
	artDesAntrags?: string,
	isCopyMode: boolean = false,
) {
	const originalAntragSkizzeData = await GetRecord(APIS.Antrag, antragSkizzeId, { customPopulate: antragsFormular });
	// Kann Skizze oder Antrag sein, je nachdem was dupliziert wird
	let originalAntragOrSkizze = removeNullDeep(removeDataKeys(get(originalAntragSkizzeData, 'data')));

	// Unique ID generieren für die neuen Einträge (z.B. Verbundpartner, KMU-Erklärungen, RUS-Erklärungen, usw.)
	const uniqueID = generateUniqueId();
	const orgaID = Number(get(originalAntragOrSkizze, P.Antragstellende_Organisation.path + '.id', null));

	let foerderProgrammId = 0;
	// Prüfen ob foerderProgramm Object übergeben wurde
	// Wenn nicht, dann aus dem originalSkizze holen
	if (foerderProgramm && !isObjectEmpty(foerderProgramm)) {
		foerderProgrammId = get(foerderProgramm, 'id', -1);
	} else {
		foerderProgrammId = get(originalAntragOrSkizze, `${P.Foerderprogramm.path}.id`, -1);
	}

	const foerderProgrammRes = await GetRecord(APIS.FörderProgramme, foerderProgrammId, POP_LEVEL.foerderprogramm.FULL);
	const foerderProgrammData = removeNullDeep(removeDataKeys(get(foerderProgrammRes, 'data')));

	const pflichtErklaerungen = get(foerderProgrammData, FP_P.Pflichterklaerungen_Antrag.path, []);
	const optionaleErklaerungen = get(foerderProgrammData, FP_P.Optionale_Erklaerungen_Antrag.path, []);
	const erforderlicheAnlagen = get(foerderProgrammData, FP_P.Erforderliche_Anlagen_Antrag.path, []);
	const optionaleAnlagen = get(foerderProgrammData, FP_P.Optionale_Anlagen_Antrag.path, []);
	const pflichterklaerungenFormantrag = get(foerderProgrammData, FP_P.Pflichterklaerungen_Formantrag.path, []);
	const optionaleErklaerungenFormantrag = get(foerderProgrammData, FP_P.Optionale_Erklaerungen_Formantrag.path, []);
	const erforderlicheAnlagenFormantrag = get(foerderProgrammData, FP_P.Erforderliche_Anlagen_Formantrag.path, []);
	const optionaleAnlagenFormantrag = get(foerderProgrammData, FP_P.Optionale_Anlagen_Formantrag.path, []);
	const duplicateSkizzeAsSkizze = isCopyMode && currentAntragsTyp === ANTRAG_TYP.Skizze;

	const defaultData = {
		...get(originalAntragOrSkizze, 'attributes'),
		...getAssoziiertePartnerData(originalAntragOrSkizze),
		Skizze:
			duplicateSkizzeAsSkizze ? null
			: currentAntragsTyp === ANTRAG_TYP.Skizze ? antragSkizzeId
			: null,
		Antragstatus_Antragstatus: ANTRAGS_STATUS.inBearbeitung,
		Allgemein_Typ: duplicateSkizzeAsSkizze ? ANTRAG_TYP.Skizze : ANTRAG_TYP.Antrag,
		Allgemein_Eingangsnummer: get(originalAntragOrSkizze, P.Allgemein_Eingangsnummer.path, null),
		Allgemein_Eingegangen_Am: null,
		Allgemein_Eingereicht_Am: null,
		Allgemein_Geaendert_Durch_Antragsteller: null,
		[getFieldNamefromDataPath(P.Allgemein_Projekttitel.path)]:
			get(originalAntragOrSkizze, P.Allgemein_Projekttitel.path) + `${currentAntragsTyp === ANTRAG_TYP.Skizze ? '' : ' (Kopie)'}`,
		// Kurzwort nur übernehmen, wenn aus Skizze zu Antrag dupliziert wird
		[getFieldNamefromDataPath(P.Allgemein_Kurzwort.path)]:
			currentAntragsTyp === ANTRAG_TYP.Skizze ? get(originalAntragOrSkizze, P.Allgemein_Kurzwort.path) : null,
		// Referenz nummer und HA Projekt Nummer leeren
		[getFieldNamefromDataPath(P.Allgemein_Referenz_Nr.path)]: null,
		[getFieldNamefromDataPath(P.Allgemein_HA_Projekt_Nr.path)]: null,
		[getFieldNamefromDataPath(P.Allgemein_Geplante_Projektlaufzeit_von.path)]: get(
			originalAntragOrSkizze,
			P.Allgemein_Geplante_Projektlaufzeit_von.path,
		),
		[getFieldNamefromDataPath(P.Allgemein_Geplante_Projektlaufzeit_bis.path)]: get(
			originalAntragOrSkizze,
			P.Allgemein_Geplante_Projektlaufzeit_bis.path,
		),
		// Antragsteller
		Antragstellende_Organisation_Bankverbindung_Bezeichnung: get(
			originalAntragOrSkizze,
			P.Antragstellende_Organisation_Bankverbindung_Bezeichnung.path,
			null,
		),
		Antragstellende_Organisation_Bankverbindung_Kontoinhaber: get(
			originalAntragOrSkizze,
			P.Antragstellende_Organisation_Bankverbindung_Kontoinhaber.path,
			null,
		),
		Antragstellende_Organisation_Bankverbindung_Geldinstitut: get(
			originalAntragOrSkizze,
			P.Antragstellende_Organisation_Bankverbindung_Geldinstitut.path,
			null,
		),
		Antragstellende_Organisation_Bankverbindung_IBAN: get(originalAntragOrSkizze, P.Antragstellende_Organisation_Bankverbindung_IBAN.path, null),
		Antragstellende_Organisation_Bankverbindung_BIC: get(originalAntragOrSkizze, P.Antragstellende_Organisation_Bankverbindung_BIC.path, null),
		Allgemein_Eingereichtes_Stammdatenblatt: null,
		Antragstellende_Organisation_Zeichnungsberechtigte: removeIdsFromArrayObjects(
			get(originalAntragOrSkizze, P.Antragstellende_Organisation_Zeichnungsberechtigte.path, []),
		),
		Antragstellende_Organisation_Foerderquote:
			currentAntragsTyp === ANTRAG_TYP.Skizze ?
				[]
			:	removeIdsFromArrayObjects(get(originalAntragOrSkizze, P.Antragstellende_Organisation_Foerderquote.path, [])),
		// Antragsteller Prüfungs-Assistenz leeren
		Antragstellende_Pruefung_Pruefstatus: null,
		Antragstellende_Pruefung_freigegeben_Am: null,
		Antragstellende_Pruefung_Ueberarbeitet_Am: null,
		Antragstellende_Pruefung_Erledigt: null,
		Antragstellende_Pruefung_Hinweis_Fuer_Den_Antragsteller: null,
		// // Verbundpartner
		// Verbundpartner: removeIdsFromArrayObjects(
		// 	get(originalSkizze, P.Verbundpartner.path, [
		// 		{
		// 			Rechtsverbindlicher_Name_Der_Organisation: '',
		// 			Position_ID: generateUniqueId(),
		// 			Verbundpartner_Ansprechpartnerin: [{ Vorname: '' }],
		// 		},
		// 	]).map((eachVP: any) => {
		// 		return {
		// 			...eachVP,
		// 			[ANTRAG_VERBUNDPARTNER.Partner]: get(eachVP, `${ANTRAG_VERBUNDPARTNER.Partner}.id`, null),
		// 			[ANTRAG_VERBUNDPARTNER.FoerderquoteGesamt]: null,
		// 			[ANTRAG_VERBUNDPARTNER.FoerderQuoteBlock]: [],
		// 			[ANTRAG_VERBUNDPARTNER.Ansprechpartner]: removeIdsFromArrayObjects(
		// 				get(eachVP, ANTRAG_VERBUNDPARTNER.Ansprechpartner, [{ Vorname: null }])
		// 			),
		// 			// Verbundpartner Prüfungs-Assistenz leeren
		// 			[ANTRAG_VERBUNDPARTNER.UeberarbeitungHinweisStatus]: null,
		// 			[ANTRAG_VERBUNDPARTNER.UeberarbeitungHinweisHinweis]: null,
		// 			[ANTRAG_VERBUNDPARTNER.UeberarbeitungHinweisErledigt]: null,
		// 		};
		// 	})
		// ),
		// // Selbst-Erklärung KMU
		// Selbsterklaerung_KMU: removeIdsFromArrayObjects(
		// 	get(originalSkizze, P.Selbsterklaerung_KMU.path, []).map((eachKmu: any) => {
		// 		return {
		// 			...eachKmu,
		// 			[KMU_PATHS.Unternehmen]: get(eachKmu, `${KMU_PATHS.Unternehmen}.id`),
		// 			[KMU_PATHS.Unternehmenskennzahlen]: removeIdsFromArrayObjects(
		// 				get(eachKmu, KMU_PATHS.Unternehmenskennzahlen, [{ Kmu_Kennzahlen_Bezugsjahr: null }, { Kmu_Kennzahlen_Bezugsjahr: null }])
		// 			),
		// 			// Dateien Leeren
		// 			[KMU_PATHS.UnterschriebeneSelbsterklaerung]: null,
		// 			[KMU_PATHS.SelbstErklaerungBerechnung]: null,
		// 			// Prüf-Assistenz leeren
		// 			[KMU_PATHS.Prufueng_Prufstatus]: null,
		// 			[KMU_PATHS.Prufueng_Erledigt]: null,
		// 			[KMU_PATHS.Prufueng_Freigegeben]: null,
		// 			[KMU_PATHS.Prufueng_HinweisAntragsteller]: null,
		// 			[KMU_PATHS.Prufueng_InternerKommentar]: null,
		// 			[KMU_PATHS.Prufueng_UeberarbeitetAm]: null,
		// 		};
		// 	})
		// ),
		// // RUS-Seblsterklärung
		// Selbsterklaerung_RUS: removeIdsFromArrayObjects(get(originalSkizze, P.Selbsterklaerung_RUS.path, [])).map((eachRus: any) => {
		// 	return {
		// 		...eachRus,
		// 		[RUS_PATH.UnterschriebeneSelbsterklaerung]: null,
		// 	};
		// }),
		...getErklaerungen(originalAntragOrSkizze, uniqueID, orgaID),
		// De-Minimis
		Deminimis_Bereits_Erhaltene_Beihilfen: removeIdsFromArrayObjects(get(originalAntragOrSkizze, P.Deminimis_Bereits_Erhaltene_Beihilfen.path, [])),
		Deminimis_Bereits_Beantragte_Beihilfen: removeIdsFromArrayObjects(
			get(originalAntragOrSkizze, P.Deminimis_Bereits_Beantragte_Beihilfen.path, []),
		),
		Deminimis_Kombination_Mit_Anderen_Beihilfen: removeIdsFromArrayObjects(get(originalAntragOrSkizze, P.Deminimis_Kombination.path, [])),
		Deminimis_Unterschriebene_Erklaerung: null,
		// De-minimis Prüfungs-Assistenz leeren
		Deminimis_Pruefung_Pruefstatus: null,
		Deminimis_Pruefung_freigegeben_Am: null,
		Deminimis_Pruefung_Ueberarbeitet_Am: null,
		Deminimis_Pruefung_Erledigt: null,
		Deminimis_Pruefung_Hinweis_Fuer_Den_Antragsteller: null,

		// Projektbeschreibung, Erklärung, Erläuterung, usw. Blocks
		...getAntragData(originalAntragOrSkizze),
		// Aktuelle Organisationsdaten zum Zeitpunkt der Duplikation
		...(await getOrganisationDaten(orgaID)),
		/**
		 * DAS MUSS NACH DEM getOrganisationDaten(orgaID) stehen.
		 * Die Ansprechpartner soll immer von Skizze überschrieben werden.
		 */
		Antragstellende_Organisation_Ansprechpartnerin: removeIdsFromArrayObjects(
			get(originalAntragOrSkizze, P.Antragstellende_Organisation_Ansprechpartnerin.path, []),
		),
		// Vorzeitiger Vorhabensbeginn
		Vorzeitiger_Vorhabenbeginn: removeIdsFromArrayObjects(get(originalAntragOrSkizze, P.Vorzeitiger_Vorhabenbeginn.path, null)),
		// Vorzeitiger Vorhabensbeginn Prüfungs-Assistenz leeren
		Vorzeitiger_Vorhabenbeginn_Pruefung_Pruefstatus: null,
		Vorzeitiger_Vorhabenbeginn_Pruefung_freigegeben_Am: null,
		Vorzeitiger_Vorhabenbeginn_Pruefung_Ueberarbeitet_Am: null,
		Vorzeitiger_Vorhabenbeginn_Pruefung_Erledigt: null,
		Vorzeitiger_Vorhabenbegin_hinnweis_Fuer_Den_Antragsteller: null,
		// Zeitplanung
		Zeitplanung_Zeitplanung_Block_1: getZeitplanung(currentAntragsTyp, originalAntragOrSkizze),
		// Zeitplanung Prüfungs-Assistenz leeren
		Zeitplanung_Pruefung_Pruefstatus: null,
		Zeitplanung_Pruefung_freigegeben_Am: null,
		Zeitplanung_Pruefung_Ueberarbeitet_Am: null,
		Zeitplanung_Pruefung_Erledigt: null,
		Zeitplanung_Pruefung_Hinweis_Fuer_Den_Antragsteller: null,
		// Eingereicht Antrag / Skizze
		Allgemein_Eingereichter_Antrag_Skizze: null,
		[getFieldNamefromDataPath(P.Allgemein_Generiertes_PDF.path)]: null,
		// Allgemeine Hinweise an die HA
		Allgemein_Hinweise_An_Die_HA:
			currentAntragsTyp === ANTRAG_TYP.Skizze ? null : get(originalAntragOrSkizze, P.Allgemein_Hinweise_An_Die_HA.path, null),
		// Foerderquote und Summe leeren (Nur wenn Skizze zu Antrag dupliziert wird)
		// Ansonsten wird die Foerderquote und Summe mit dupliziert
		Allgemein_Beantragte_Zuwendung:
			currentAntragsTyp === ANTRAG_TYP.Skizze ? null : get(originalAntragOrSkizze, P.Allgemein_Beantragte_Zuwendung.path, null),
		Allgemein_Eingebrachte_Eigen_Und_Drittmittel:
			currentAntragsTyp === ANTRAG_TYP.Skizze ? null : get(originalAntragOrSkizze, P.Allgemein_Eingebrachte_Eigen_Und_Drittmittel.path, null),
		Allgemein_Geplante_Projektausgaben:
			currentAntragsTyp === ANTRAG_TYP.Skizze ? null : get(originalAntragOrSkizze, P.Allgemein_Geplante_Projektausgaben.path, null),
		Bewilligung_Foerderquote_Gesamt:
			currentAntragsTyp === ANTRAG_TYP.Skizze ? null : get(originalAntragOrSkizze, P.Bewilligung_Foerderquote_Gesamt.path, null),
		Orga_Foerderquote_Gesamt: currentAntragsTyp === ANTRAG_TYP.Skizze ? null : get(originalAntragOrSkizze, P.Orga_Foerderquote_Gesamt.path, null),
		// Erklärung und Anlagen
		Erklaerungen_Pflichterklaerungen: removeIdsFromArrayObjects(pflichtErklaerungen),
		Erklaerungen_Optionale_Erklaerungen: removeIdsFromArrayObjects(optionaleErklaerungen),
		Anlagen_Erforderliche_Anlagen: removeIdsFromArrayObjects(erforderlicheAnlagen),
		Anlagen_Optionale_Anlagen: removeIdsFromArrayObjects(optionaleAnlagen),
		...getSonstigeAnlagen(antragsFormular),
		// Bescheid leeren
		[getFieldNamefromDataPath(P.Foerderbescheid_Foerderbescheid.path)]: null,
		// Rechtsbehelf leeren
		[getFieldNamefromDataPath(P.Angaben_Zum_Rechtsbehelf_Datei.path)]: null,
		[getFieldNamefromDataPath(P.Angaben_Zum_Rechtsbehelf_Anmerkung.path)]: null,
		[getFieldNamefromDataPath(P.Angaben_Zum_Rechtsbehelf_Einreichdatum.path)]: null,
		// Kostenposition werden separat dupliziert durch die Funktion DuplicateKostenPosition
		//  deswegen hier auf null setzen
		Foerderantrag_Kostenpositions: null,
	};
	// Formantrag nimmt die Kopie vom Antrag und überschreibt die spezifischen Felder, die im Formantrag anders sein sollen
	const formantragData = {
		...defaultData,
		[getFieldNamefromDataPath(P.Allgemein_Art_Des_Antrags.path)]: ART_DES_ANTRAGS.Formantrag,
		[getFieldNamefromDataPath(P.urspruenglicher_Antrag.path)]: antragSkizzeId,
		[getFieldNamefromDataPath(P.Projektbeschreibung_Loewe.path)]: null,
		[getFieldNamefromDataPath(P.Allgemein_Kurzwort.path)]: get(originalAntragOrSkizze, P.Allgemein_Kurzwort.path),
		[getFieldNamefromDataPath(P.Allgemein_Eingangsnummer.path)]: get(originalAntragOrSkizze, P.Allgemein_Eingangsnummer.path),
		[getFieldNamefromDataPath(P.Allgemein_HA_Projekt_Nr.path)]: get(originalAntragOrSkizze, P.Allgemein_HA_Projekt_Nr.path),
		//Zeitplanung
		// Zeitplanung_Zeitplanung_Block_1: [],
		// Erklärung und Anlagen
		Erklaerungen_Pflichterklaerungen: removeIdsFromArrayObjects(pflichterklaerungenFormantrag),
		Erklaerungen_Optionale_Erklaerungen: removeIdsFromArrayObjects(optionaleErklaerungenFormantrag),
		Anlagen_Erforderliche_Anlagen: removeIdsFromArrayObjects(erforderlicheAnlagenFormantrag),
		Anlagen_Optionale_Anlagen: removeIdsFromArrayObjects(optionaleAnlagenFormantrag),
	};

	const antragData = {
		data: artDesAntrags === ART_DES_ANTRAGS.Formantrag ? formantragData : defaultData,
	};

	let antragRes = await PostRecord(APIS.Antrag, antragData);

	if (antragRes) {
		await DuplicateKostenPosition(
			get(antragRes, 'data.id'),
			antragsFormular,
			get(originalAntragOrSkizze, P.Foerderantrag_Kostenpositions.path, []),
			orgaID,
		);
	}

	// return antragData;
	return antragRes;
}
export { DuplicateAntrag };

async function DuplicateKostenPosition(antragId: number, antragsformular: string, allKostenPositions: any[], orgaID: number) {
	if (isArray(allKostenPositions) && allKostenPositions.length > 0) {
		allKostenPositions.forEach(async (eachKostenPosition: any) => {
			let newKPData = {
				data: {
					...get(eachKostenPosition, 'attributes'),
					Foerderantrag: antragId,
					Unternehmen: get(eachKostenPosition, `${KP_P.Unternehmen.path}.id`),
					Allgemein_Kostenkategorie: get(eachKostenPosition, `${KP_P.Allgemein_Kostenkategorie.path}.id`),
					// Allgemeine Kosten Pro Jahr
					Allgemein_Kosten_Pro_Jahr: removeIdsFromArrayObjects(
						get(eachKostenPosition, KP_P.Allgemein_Kosten_Pro_Jahr.path, [{ Zelle_1: null }]),
					),
					...removeNullDeep(getKostenPositionKalkulationBlock(eachKostenPosition, antragsformular)),
				},
			};
			await PostRecord(APIS.Kostenposition, newKPData);
		});
	} else {
		const filter = withKostenkategorieEinsatzbereichFilter(
			{
				Allgemein_Antragsformular: { $contains: antragsformular },
				Allgemein_Aktiv: { $eq: true },
			},
			APIS.Antrag,
		);
		const res = await GetRecords(APIS.Kostenkategorie, [], filter);
		const kostenKategories = removeNullDeep(removeDataKeys(get(res, 'data', [])));

		await handleCreateKostenPositions(orgaID, antragId, kostenKategories);
	}
}

function getAntragData(application: any) {
	const antragsFormular = get(application, P.Allgemein_Antragsformular.path, '');

	let antragData;

	switch (antragsFormular) {
		case ANTRAGS_FORMULAREN.EmobFundE:
			antragData = getEmobFuEBeschreibung(application);
			break;
		case ANTRAGS_FORMULAREN.EmobInvest:
			antragData = getEmobInvestBeschreibung(application);
			break;
		case ANTRAGS_FORMULAREN.EmobLis:
			antragData = getEmobLisBeschreibung(application);
			break;
		case ANTRAGS_FORMULAREN.Messefoerderung:
			antragData = getMesseBeschreibung(application);
			break;
		case ANTRAGS_FORMULAREN.Kreativwirtschaft:
			antragData = getKreativwirtschaftBeschreibung(application);
			break;
		case ANTRAGS_FORMULAREN.Loewe:
			antragData = getLoeweBeschreibung(application);
			break;
		default:
			antragData = {};
	}
	// Zentrale Filterung leerer Objekte
	const filteredObject = filterEmptyObjects(antragData) || {};
	return filteredObject;
}

const getKostenPositionKalkulationBlock = (kostenPosition: any, antragsformular: string) => {
	let kalkulationData;

	switch (antragsformular) {
		case ANTRAGS_FORMULAREN.EmobFundE:
			kalkulationData = getEmobFuEKalkulation(kostenPosition);
			break;
		case ANTRAGS_FORMULAREN.EmobInvest:
			kalkulationData = getEmobInvestKalkulation(kostenPosition);
			break;
		case ANTRAGS_FORMULAREN.EmobLis:
			kalkulationData = getEmobLisKalkulation(kostenPosition);
			break;
		case ANTRAGS_FORMULAREN.Messefoerderung:
			kalkulationData = {};
			break;
		case ANTRAGS_FORMULAREN.Kreativwirtschaft:
			kalkulationData = getKreativKalkulation(kostenPosition);
			break;
		case ANTRAGS_FORMULAREN.Loewe:
			kalkulationData = {};
			break;
		default:
			kalkulationData = {};
	}

	// Zentrale Filterung leerer Objekte
	return filterEmptyObjects(kalkulationData) || {};
};
/**
 * ************************************************************************************************************************************************************
 * **************************************************************** Delete Antrag *****************************************************************************
 * ************************************************************************************************************************************************************
 */
export async function DeleteAntrag(antragId: number) {
	const errors: string[] = [];

	try {
		const antragRes = await GetRecord(APIS.Antrag, antragId, ['Foerderantrag_Kostenpositions'], [], [], [], ['Antragstatus_Antragstatus']);
		const antrag = removeDataKeys(removeNullDeep(antragRes));

		const allKostenPositions = get(antrag, P.Foerderantrag_Kostenpositions.path, []);
		for (let i = 0; i < allKostenPositions.length; i++) {
			const eachKostenPosition = allKostenPositions[i];
			const eachKostenPositionId = get(eachKostenPosition, 'id');
			try {
				await DeleteRecord(APIS.Kostenposition, eachKostenPositionId, false);
			} catch (err) {
				console.error(`ERROR: Deleting Kostenposition ID ${eachKostenPositionId}: `, err);
				errors.push(`Failed to delete Kostenposition ID ${eachKostenPositionId}`);
			}
		}

		try {
			await DeleteRecord(APIS.Antrag, antragId, false);
		} catch (err) {
			console.error(`ERROR: Deleting Antrag ID ${antragId}: `, err);
			errors.push(`Failed to delete Antrag ID ${antragId}`);
		}

		return { success: errors.length === 0, errors };
	} catch (err: any) {
		console.error('ERROR: Fetching Antrag or processing deletion: ', err);
		errors.push('Failed to fetch Antrag or process deletions');
		return { success: false, errors };
	}
}

/**
 * ************************************************************************************************************************************************************
 * ********************************************************* Organisations Daten ******************************************************************************
 * ************************************************************************************************************************************************************
 */
async function getOrganisationDaten(orgaID: number) {
	const result: any = {};

	const res = await GetRecord(APIS.Organisation, orgaID, POP_LEVEL.organisation.FULL);
	const orgaData = removeNullDeep(removeDataKeys(get(res, 'data', {})));

	const orgaDataFuerAntrag = getOrgaDateFuerAntrag(orgaData);
	// This lodash function loops throug a JSON.
	// The parmeter for the callback-function are (value, key)
	forOwn(orgaDataFuerAntrag, (value: string, key: string) => {
		set(result, key, value);
	});

	return result;
}

/**
 * ************************************************************************************************************************************************************
 * ************************************************ Selbsterklärung KMU und RUS-Erklärung *********************************************************************
 * ************************************************************************************************************************************************************
 */
function getErklaerungen(application: any, positionID: string, orgaID: number) {
	const result: any = {};
	const verbundpartner = get(application, P.Verbundpartner.path, []);
	const kmuErklaerungen = get(application, P.Selbsterklaerung_KMU.path, []);
	const rusErklaerungen = get(application, P.Selbsterklaerung_RUS.path, []);

	const loiVorhandenVPKey = getFieldNamefromDataPath(P.Verbundpartner.schema.attributes.LOI_Vorhanden.path);
	const letterOfIntentKey = getFieldNamefromDataPath(P.Verbundpartner.schema.attributes.Letter_Of_Intent.path);
	// Getting Verbundpartner
	if (isArray(verbundpartner) && verbundpartner.length > 0) {
		result.Verbundpartner = removeIdsFromArrayObjects(
			verbundpartner.map((eachVP: any) => {
				const loiId = get(eachVP, 'Letter_Of_Intent.id', null);
				const isLoiVorhanden = Boolean(loiId || get(eachVP, loiVorhandenVPKey, false));
				return {
					...eachVP,
					[ANTRAG_VERBUNDPARTNER.Partner]: get(eachVP, `${ANTRAG_VERBUNDPARTNER.Partner}.id`, null),
					[ANTRAG_VERBUNDPARTNER.FoerderquoteGesamt]: null,
					[ANTRAG_VERBUNDPARTNER.FoerderQuoteBlock]: [],
					[ANTRAG_VERBUNDPARTNER.Ansprechpartner]: removeIdsFromArrayObjects(
						get(eachVP, ANTRAG_VERBUNDPARTNER.Ansprechpartner, [{ Vorname: null }]),
					),
					[letterOfIntentKey]: null,
					...(isLoiVorhanden ? { [loiVorhandenVPKey]: true } : {}),
					// Verbundpartner Prüfungs-Assistenz leeren
					[ANTRAG_VERBUNDPARTNER.UeberarbeitungHinweisStatus]: null,
					[ANTRAG_VERBUNDPARTNER.UeberarbeitungHinweisHinweis]: null,
					[ANTRAG_VERBUNDPARTNER.UeberarbeitungHinweisErledigt]: null,
				};
			}),
		);
	} else {
		result.Verbundpartner = [
			{
				Rechtsverbindlicher_Name_Der_Organisation: '',
				Position_ID: positionID,
				Verbundpartner_Ansprechpartnerin: [{ Vorname: '' }],
			},
		];
	}

	// Selbsterklärungen KMU
	if (isArray(kmuErklaerungen) && kmuErklaerungen.length > 0) {
		result.Selbsterklaerung_KMU = removeIdsFromArrayObjects(
			kmuErklaerungen.map((eachKmu: any) => {
				return {
					...eachKmu,
					[KMU_PATHS.Unternehmen]: get(eachKmu, `${KMU_PATHS.Unternehmen}.id`),
					[KMU_PATHS.Unternehmenskennzahlen]: removeIdsFromArrayObjects(
						get(eachKmu, KMU_PATHS.Unternehmenskennzahlen, [{ Kmu_Kennzahlen_Bezugsjahr: null }, { Kmu_Kennzahlen_Bezugsjahr: null }]),
					),
					// Dateien Leeren
					[KMU_PATHS.UnterschriebeneSelbsterklaerung]: null,
					[KMU_PATHS.SelbstErklaerungBerechnung]: null,
					// Prüf-Assistenz leeren
					[KMU_PATHS.Prufueng_Prufstatus]: null,
					[KMU_PATHS.Prufueng_Erledigt]: null,
					[KMU_PATHS.Prufueng_Freigegeben]: null,
					[KMU_PATHS.Prufueng_HinweisAntragsteller]: null,
					[KMU_PATHS.Prufueng_UeberarbeitetAm]: null,
				};
			}),
		);
	} else {
		result.Selbsterklaerung_KMU = [
			{
				[KMU_PATHS.Unternehmen]: orgaID,
				[KMU_PATHS.Unternehmenskennzahlen]: [{ Kmu_Kennzahlen_Bezugsjahr: null }, { Kmu_Kennzahlen_Bezugsjahr: null }],
			},
			{
				[KMU_PATHS.PositionID]: positionID,
				[KMU_PATHS.Unternehmenskennzahlen]: [{ Kmu_Kennzahlen_Bezugsjahr: null }, { Kmu_Kennzahlen_Bezugsjahr: null }],
			},
		];
	}

	// RUS-Selbsterklärungen
	if (isArray(rusErklaerungen) && rusErklaerungen.length > 0) {
		result.Selbsterklaerung_RUS = removeIdsFromArrayObjects(
			rusErklaerungen.map((eachRus: any) => {
				return {
					...eachRus,
					[RUS_PATH.Unternehmen]: orgaID,
					[RUS_PATH.UnterschriebeneSelbsterklaerung]: null,
					// Pruefs-Assistenz leeren
					[RUS_PATH.Prufueng_Prufstatus]: null,
					[RUS_PATH.Prufueng_Freigegeben]: null,
					[RUS_PATH.Prufueng_UeberarbeitetAm]: null,
					[RUS_PATH.Prufueng_Erledigt]: null,
					[RUS_PATH.Prufueng_HinweisAntragsteller]: null,
				};
			}),
		);
	} else {
		result.Selbsterklaerung_RUS = [
			{
				[RUS_PATH.Unternehmen]: orgaID,
				[RUS_PATH.UnterschriebeneSelbsterklaerung]: null,
			},
			{
				[RUS_PATH.PositionID]: positionID,
				[RUS_PATH.UnterschriebeneSelbsterklaerung]: null,
			},
		];
	}

	return result;
}

/**
 * ************************************************************************************************************************************************************
 * ************************************************ Projektbeschreibung Blocks für Anträge ********************************************************************
 * ************************************************************************************************************************************************************
 */
function getEmobFuEBeschreibung(application: any) {
	const projektBeschreibungEmobFundE: any = {
		Projektbeschreibung_Elektromobilitaet_FE: {
			...get(application, P.Projektbeschreibung_Elektromobilitaet_FE.path),
			// AUsgangsituation
			Projektbeschreibung_Elek_0009162530_FE_Ausgangssituation_Bilder: [{ Kurzbeschreibung: '' }],
			Projektbeschreibung_Elek_0213069123_tuation_Pruefung_Pruefstatus: null,
			Projektbeschreibung_Elek_0235835085_tion_Pruefung_freigegeben_Am: null,
			Projektbeschreibung_Elek_0267605373_on_Pruefung_Ueberarbeitet_Am: null,
			Projektbeschreibung_Elek_0217627076_ssituation_Pruefung_Erledigt: null,
			Projektbeschreibung_Elek_0266237954_nweis_Fuer_Den_Antragsteller: null,

			// Beschreibung des Vorhabens
			Projektbeschreibung_Elek_0178136770_reibung_Des_Vorhabens_Bilder: [{ Kurzbeschreibung: '' }],
			Projektbeschreibung_Elek_0089835347_rhabens_Pruefung_Pruefstatus: null,
			Projektbeschreibung_Elek_0132413229_bens_Pruefung_freigegeben_Am: null,
			Projektbeschreibung_Elek_0099269789_ns_Pruefung_Ueberarbeitet_Am: null,
			Projektbeschreibung_Elek_0106506068_Vorhabens_Pruefung_Erledigt: null,
			Projektbeschreibung_Elek_0096703458_nweis_Fuer_Den_Antragsteller: null,

			// Marktausblick
			Projektbeschreibung_Elek_0139433634_sblick_Und_Verwertung_Bilder: [{ Kurzbeschreibung: '' }],
			Projektbeschreibung_Elek_0149595523_wertung_Pruefung_Pruefstatus: null,
			Projektbeschreibung_Elek_0126274205_tung_Pruefung_freigegeben_Am: null,
			Projektbeschreibung_Elek_0121048317_ng_Pruefung_Ueberarbeitet_Am: null,
			Projektbeschreibung_Elek_0074775636_Verwertung_Pruefung_Erledigt: null,
			Projektbeschreibung_Elek_0119161730_nweis_Fuer_Den_Antragsteller: null,

			// SWOT
			Projektbeschreibung_Elek_0260019443_erungen_Pruefung_Pruefstatus: null,
			Projektbeschreibung_Elek_0045604109_ngen_Pruefung_freigegeben_Am: null,
			Projektbeschreibung_Elek_0138451405_en_Pruefung_Ueberarbeitet_Am: null,
			Projektbeschreibung_Elek_0023299332_orderungen_Pruefung_Erledigt: null,
			Projektbeschreibung_Elek_0074298450_nweis_Fuer_Den_Antragsteller: null,

			Erklaerung_Elektromobilitaet_FE_Skizze: null,
			Erlaeuterung_Zu_Den_Anlagen_Elektromobilitaet_FE_Skizze: null,
		},
	};
	unset(projektBeschreibungEmobFundE, 'Projektbeschreibung_Elektromobilitaet_FE.id');
	return projektBeschreibungEmobFundE;
}
function getEmobInvestBeschreibung(application: any) {
	const projektBeschreibungEmobInvest: any = {
		Projektbeschreibung_Elektromobilitaet_Invest: {
			...get(application, P.Projektbeschreibung_Elektromobilitaet_Invest.path),
			// Ausgangssituation
			Projektbeschreibung_Elek_0095548994_est_Ausgangssituation_Bilder: [{ Kurzbeschreibung: '' }],
			// Ausgangssituation Prüfung-Assistenz leeren
			Projektbeschreibung_Elek_0109726323_tuation_Pruefung_Pruefstatus: null,
			Projektbeschreibung_Elek_0124343549_tion_Pruefung_freigegeben_Am: null,
			Projektbeschreibung_Elek_0180972573_on_Pruefung_Ueberarbeitet_Am: null,
			Projektbeschreibung_Elek_0243997268_ssituation_Pruefung_Erledigt: null,
			Projektbeschreibung_Elek_0250110818_nweis_Fuer_Antragsteller: null,
			// Technische Spezifikation Elektrofahrzeuge
			Projektbeschreibung_Elek_0112109858_ionen_Elektrofahrzeug_Bilder: [{ Kurzbeschreibung: '' }],
			// Technische Spezifikation Elektrofahrzeuge Prüfung-Assistenz leeren
			Projektbeschreibung_Elek_0097856355_ahrzeug_Pruefung_Pruefstatus: null,
			Projektbeschreibung_Elek_0134768557_zeug_Pruefung_freigegeben_Am: null,
			Projektbeschreibung_Elek_0165294973_ug_Pruefung_Ueberarbeitet_Am: null,
			Projektbeschreibung_Elek_0211431332_rofahrzeug_Pruefung_Erledigt: null,
			Projektbeschreibung_Elek_0166927874_nweis_Fuer_Den_Antragsteller: null,
			// Spezifikation des zu beschaffenden E-Fahrzeugs
			Spezifikation_Des_Zu_Beschaffenden_E_Fahrzeugs: {
				...get(application, P.Projektbeschreibung_Elektromobilitaet_Invest.schema.attributes.Spezifikation_Des_Zu_Beschaffenden_E_Fahrzeugs.path),
				Bilder: [{ Kurzbeschreibung: '' }],
			},
			// Technische Spezifikationen Ladeinfrastruktur
			Projektbeschreibung_Elek_0231047921_en_Ladeinfrastruktur_Block_1: [{ Zelle_1: '' }],
			Projektbeschreibung_Elek_0193290210_nen_Ladeinfrastruktur_Bilder: [{ Kurzbeschreibung: '' }],
			// Technische Spezifikationen Ladeinfrastruktur Prüfung-Assistenz leeren
			Projektbeschreibung_Elek_0035907651_truktur_Pruefung_Pruefstatus: null,
			Projektbeschreibung_Elek_0248217693_ktur_Pruefung_freigegeben_Am: null,
			Projektbeschreibung_Elek_0217957821_ur_Pruefung_Ueberarbeitet_Am: null,
			Projektbeschreibung_Elek_0145248452_rastruktur_Pruefung_Erledigt: null,
			Projektbeschreibung_Elek_0148179138_nweis_Fuer_Den_Antragsteller: null,
			// Wasserstoff-Betankungsanlage
			Projektbeschreibung_Elek_0103379282_toff_Betankungsanlage_Bilder: [{ Kurzbeschreibung: '' }],
			// Wasserstoff-Betankungsanlage Prüfung-Assistenz leeren
			Projektbeschreibung_Elek_0235756515_sanlage_Pruefung_Pruefstatus: null,
			Projektbeschreibung_Elek_0007924861_lage_Pruefung_freigegeben_Am: null,
			Projektbeschreibung_Elek_0156363533_ge_Pruefung_Ueberarbeitet_Am: null,
			Projektbeschreibung_Elek_0227709476_ungsanlage_Pruefung_Erledigt: null,
			Projektbeschreibung_Elek_0159442450_nweis_Fuer_Den_Antragsteller: null,
			// Ausschreibung und Vergabe
			Projektbeschreibung_Elek_0220424306_chreibung_Und_Vergabe_Bilder: [{ Kurzbeschreibung: '' }],
			// Ausschreibung und Vergabe Prüfung-Assistenz leeren
			Projektbeschreibung_Elek_0068448995_Vergabe_Pruefung_Pruefstatus: null,
			Projektbeschreibung_Elek_0156362349_gabe_Pruefung_freigegeben_Am: null,
			Projektbeschreibung_Elek_0039580205_be_Pruefung_Ueberarbeitet_Am: null,
			Projektbeschreibung_Elek_0251802548_nd_Vergabe_Pruefung_Erledigt: null,
			Projektbeschreibung_Elek_0108452210_nweis_Fuer_Den_Antragsteller: null,
			// Sonstige Informationen
			Projektbeschreibung_Elek_0109936114_onstige_Informationen_Bilder: [{ Kurzbeschreibung: '' }],
			// Sonstige Informationen Prüfung-Assistenz leeren
			Projektbeschreibung_Elek_0167902019_ationen_Pruefung_Pruefstatus: null,
			Projektbeschreibung_Elek_0215517309_onen_Pruefung_freigegeben_Am: null,
			Projektbeschreibung_Elek_0167110573_en_Pruefung_Ueberarbeitet_Am: null,
			Projektbeschreibung_Elek_0127455972_ormationen_Pruefung_Erledigt: null,
			Projektbeschreibung_Elek_0165473010_nweis_Fuer_Den_Antragsteller: null,
		},
	};
	unset(projektBeschreibungEmobInvest, 'Projektbeschreibung_Elektromobilitaet_Invest.id');
	return projektBeschreibungEmobInvest;
}
function getEmobLisBeschreibung(application: any) {
	const projektBeschreibungEmobLis: any = {
		Projektbeschreibung_Elektromobilitaet_LIS: {
			...get(application, P.Projektbeschreibung_Elektromobilitaet_LIS.path),
			// Fördervorausetzungen Prüfung-Assistenz leeren
			Projektbeschreibung_Elek_0148514099_tzungen_Pruefung_Pruefstatus: null,
			Projektbeschreibung_Elek_0117840557_ngen_Pruefung_freigegeben_Am: null,
			Projektbeschreibung_Elek_0126303357_en_Pruefung_Ueberarbeitet_Am: null,
			Projektbeschreibung_Elek_0209715028_ssetzungen_Pruefung_Erledigt: null,
			Projektbeschreibung_Elek_0128586498_nweis_Fuer_Den_Antragsteller: null,

			// Ausgangsituation Prüfung-Assistenz leeren
			Projektbeschreibung_Elek_0030170675_tuation_Pruefung_Pruefstatus: null,
			Projektbeschreibung_Elek_0024974269_tion_Pruefung_freigegeben_Am: null,
			Projektbeschreibung_Elek_0244197901_on_Pruefung_Ueberarbeitet_Am: null,
			Projektbeschreibung_Elek_0231697972_ssituation_Pruefung_Erledigt: null,
			Projektbeschreibung_Elek_0045031698_nweis_Fuer_Den_Antragsteller: null,
			// Genehmigung Eigentümer
			Genehmigung_Eigentuemer: null,
			// Standorte
			Standort_Spezifikation_Ladeinfrastruktur: get(
				application,
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.path,
				[],
			).map((eachStandort: any) => {
				return {
					...eachStandort,
					[EMOB_LIS_STANDORTE_PATHS.DarlegungBlock]: removeIdsFromArrayObjects(
						get(eachStandort, EMOB_LIS_STANDORTE_PATHS.DarlegungBlock, [{ Zelle_1: '' }]),
					),
					[EMOB_LIS_STANDORTE_PATHS.DarstellungDerAufstellorte]: null,
					// Each-Standort Prüfung-Assistenz leeren
					[EMOB_LIS_STANDORTE_PATHS.Prufueng_Prufstatus]: null,
					[EMOB_LIS_STANDORTE_PATHS.Prufueng_Freigegeben]: null,
					[EMOB_LIS_STANDORTE_PATHS.Prufueng_UeberarbeitetAm]: null,
					[EMOB_LIS_STANDORTE_PATHS.Prufueng_Erledigt]: null,
					[EMOB_LIS_STANDORTE_PATHS.Prufueng_HinweisAntragsteller]: null,
				};
			}),
			// Sonstige Informationen Prüfung-Assistenz leeren
			Projektbeschreibung_Elek_0239910483_ationen_Pruefung_Pruefstatus: null,
			Projektbeschreibung_Elek_0027098173_onen_Pruefung_freigegeben_Am: null,
			Projektbeschreibung_Elek_0249670381_en_Pruefung_Ueberarbeitet_Am: null,
			Projektbeschreibung_Elek_0063542836_ormationen_Pruefung_Erledigt: null,
			Projektbeschreibung_Elek_0047227314_nweis_Fuer_Den_Antragsteller: null,
		},
	};
	unset(projektBeschreibungEmobLis, 'Projektbeschreibung_Elektromobilitaet_LIS.id');
	return projektBeschreibungEmobLis;
}
function getMesseBeschreibung(application: any) {
	const Angaben_Zur_Messebeteiligung: any = {
		Angaben_Zur_Messebeteiligung: {
			...get(application, P.Angaben_Zur_Messebeteiligung.path),
			Projektbeschreibung_Messefoerderung_Messeteilnahmen: removeIdsFromArrayObjects(
				get(application, P.Angaben_Zur_Messebeteiligung.schema.attributes.Projektbeschreibung_Messefoerderung_Messeteilnahmen.path, []),
			),
			// Prüfstatus leeren
			Angaben_Zur_Messebeteiligung_Pruefung_Pruefstatus: null,
			Angaben_Zur_Messebeteiligung_Pruefung_freigegeben_Am: null,
			Angaben_Zur_Messebeteiligung_Pruefung_Ueberarbeitet_Am: null,
			Angaben_Zur_Messebeteiligung_Pruefung_Erledigt: null,
			Angaben_Zur_Messebeteili_0142701586_nweis_Fuer_Den_Antragsteller: null,
		},
	};
	unset(Angaben_Zur_Messebeteiligung, 'Angaben_Zur_Messebeteiligung.id');
	return Angaben_Zur_Messebeteiligung;
}
function getKreativwirtschaftBeschreibung(application: any) {
	const Projektbeschreibung_Kreativwirtschaft: any = {
		Projektbeschreibung_Kreativwirtschaft: {
			...get(application, P.Angaben_Zur_Messebeteiligung.path),
			// Projektbeschreibung Prüfung-Assistenz leeren
			Pruefung_Projektbeschreibung_Pruefstatus: null,
			Pruefung_Projektbeschreibung_freigegeben_Am: null,
			Pruefung_Projektbeschreibung_Ueberarbeitet_Am: null,
			Pruefung_Projektbeschreibung_Erledigt: null,
			Pruefung_Projektbeschreibung_Hinweis_Fuer_Den_Antragsteller: null,
			// Projektdurchführung Prüfung-Assistenz leeren
			Pruefung_Projektdurchfuehrung_Pruefstatus: null,
			Pruefung_Projektdurchfuehrung_freigegeben_Am: null,
			Pruefung_Projektdurchfuehrung_Ueberarbeitet_Am: null,
			Pruefung_Projektdurchfuehrung_Erledigt: null,
			Pruefung_Projektdurchfuehrung_Hinweis_Fuer_Den_Antragsteller: null,
			// Nachweis der Effektivität und Effizienz der Maßnahme Prüfung-Assistenz leeren
			Pruefung_Effizienz_Pruefstatus: null,
			Pruefung_Effizienz_freigegeben_Am: null,
			Pruefung_Effizienz_Ueberarbeitet_Am: null,
			Pruefung_Effizienz_Erledigt: null,
			Pruefung_Effizienz_Hinweis_Fuer_Den_Antragsteller: null,
		},
	};
	unset(Projektbeschreibung_Kreativwirtschaft, 'Projektbeschreibung_Kreativwirtschaft.id');
	return Projektbeschreibung_Kreativwirtschaft;
}
function getLoeweBeschreibung(application: any) {
	const projektBeschreibungLoewe: any = {
		Projektbeschreibung_Loewe: {
			...get(application, P.Projektbeschreibung_Loewe.path),
			Aktueller_Stand_Technik_Skizze: null,
			Projektziel_Und_Entwicklungsstand_Skizze: null,
			Marktausblick_Verwertung_Skizze: null,
			Wissenschaftliche_Effekte_Skizze: null,
			Kurzen_Zusammenfassung_Des_Projektes: null,
			// Ausgangssituation_Bilder: [{ Kurzbeschreibung: '' }],

			Gegenwaertiger_Stand_Technik_Bilder: [{ Kurzbeschreibung: '' }],
			Notwendigkeit_Einer_Neuartigen_Loesung_Bilder: [{ Kurzbeschreibung: '' }],
			Zielstellung_Und_Anforderungsprofil_Bilder: [{ Kurzbeschreibung: '' }],
			Innovationsgrad_Und_Alleinstellungsmerkmale_Bilder: [{ Kurzbeschreibung: '' }],
			Kernkompetenzen_Der_Verbundpartner_Bilder: [{ Kurzbeschreibung: '' }],

			Gegenwaertiger_Entwicklungsstand_Patentrecherche_Bilder: [{ Kurzbeschreibung: '' }],
			Technologische_Realisierung_Und_Entwicklungsschritte_Bilder: [{ Kurzbeschreibung: '' }],
			Massnahmen_Einer_Kommerzialisierung_Bilder: [{ Kurzbeschreibung: '' }],
			Begruendung_Einer_Staatlichen_Foerderung_Bilder: [{ Kurzbeschreibung: '' }],

			Alleinstellungsmerkmale_Bilder: [{ Kurzbeschreibung: '' }],
			Marktanalyse_Wettbewerbstechnologien_Bilder: [{ Kurzbeschreibung: '' }],
			Anwendungsgebiete_Und_Kundennutzen_Bilder: [{ Kurzbeschreibung: '' }],
			Markteintrittsstrategie_Markteinfuehrungserfolge_Bilder: [{ Kurzbeschreibung: '' }],
			Moegliche_Markteintrittsbarrieren_Bilder: [{ Kurzbeschreibung: '' }],
			Schutzrechte_Lizenzen_Zwischen_Verbundpartnern_Bilder: [{ Kurzbeschreibung: '' }],
			Unternehmensentwicklung_Erwartete_Arbeitsplatzeffekte_Bilder: [{ Kurzbeschreibung: '' }],

			// Entwicklungsvorhaben_Bilder: [{ Kurzbeschreibung: '' }],
			// Marktanalyse_Bilder: [{ Kurzbeschreibung: '' }],
			Wissenschaftliche_Effekte_Bilder: [{ Kurzbeschreibung: '' }],
			Meilensteinplanung_Bilder: [{ Kurzbeschreibung: '' }],
			Aussendarstellung_Bilder: [{ Kurzbeschreibung: '' }],
		},
		Erklaerungen_Loewe: null,
		Erlaeuterung_Zu_Den_Anlagen_Loewe_Skizze: null,
	};
	unset(projektBeschreibungLoewe, 'Projektbeschreibung_Loewe.id');
	return projektBeschreibungLoewe;
}

function getSonstigeAnlagen(antragsFormular: string) {
	switch (antragsFormular) {
		case ANTRAGS_FORMULAREN.Loewe:
			return {
				Anlagen_Sonstige_Anlagen: [
					{ [getFieldNamefromDataPath(P.Anlagen_Sonstige_Anlagen.schema.attributes.Bezeichnung_Des_Dokuments.path)]: '' },
				],
			};
		default:
			return;
	}
}

/**
 * ************************************************************************************************************************************************************
 * *********************************************** Kalkulation Blocks für Kostenpositionen ********************************************************************
 * ************************************************************************************************************************************************************
 */

function getEmobFuEKalkulation(kostenPosition: any) {
	const Personalausgaben_Emob_Antrag: any = {
		Personalausgaben_Emob_Antrag: {
			...get(kostenPosition, KP_P.Personalausgaben_Emob_Antrag.path),
			Emob_Antrag_Projekt_Jahr: removeIdsFromArrayObjects(
				get(kostenPosition, KP_P.Personalausgaben_Emob_Antrag.schema.attributes.Emob_Antrag_Projekt_Jahr.path, [{ Zelle_1: null }]),
			),
		},
	};
	unset(Personalausgaben_Emob_Antrag, 'Personalausgaben_Emob_Antrag.id');
	return Personalausgaben_Emob_Antrag;
}
function getEmobInvestKalkulation(kostenPosition: any) {
	const Kalkulation_Elektrofahrzeug: any = {
		Kalkulation_Elektrofahrzeug: {
			...get(kostenPosition, KP_P.Kalkulation_Elektrofahrzeug.path),
		},
	};
	unset(Kalkulation_Elektrofahrzeug, 'Kalkulation_Elektrofahrzeug.id');

	const Kalkulation_Lade_Tankinfrastruktur: any = {
		Kalkulation_Lade_Tankinfrastruktur: {
			...get(kostenPosition, KP_P.Kalkulation_Lade_Tankinfrastruktur.path),
		},
	};
	unset(Kalkulation_Lade_Tankinfrastruktur, 'Kalkulation_Lade_Tankinfrastruktur.id');

	const Kalkulation_Netzanschluss_Und_Erdarbeiten: any = {
		Kalkulation_Netzanschluss_Und_Erdarbeiten: {
			...get(kostenPosition, KP_P.Kalkulation_Netzanschluss_Und_Erdarbeiten.path),
		},
	};
	unset(Kalkulation_Netzanschluss_Und_Erdarbeiten, 'Kalkulation_Netzanschluss_Und_Erdarbeiten.id');
	return { ...Kalkulation_Elektrofahrzeug, ...Kalkulation_Lade_Tankinfrastruktur, ...Kalkulation_Netzanschluss_Und_Erdarbeiten };
}
function getEmobLisKalkulation(kostenPosition: any) {
	const Kalkulation_Ladeinfrastruktur_Lis = {
		Kalkulation_Ladeinfrastruktur_Lis: {
			...get(kostenPosition, KP_P.Kalkulation_Ladeinfrastruktur_Lis.path),
		},
	};
	unset(Kalkulation_Ladeinfrastruktur_Lis, 'Kalkulation_Ladeinfrastruktur_Lis.id');
	return Kalkulation_Ladeinfrastruktur_Lis;
}
function getKreativKalkulation(kostenPosition: any) {
	const Personalausgaben_Kreativwirtschaft: any = {
		Personalausgaben_Kreativwirtschaft: {
			...get(kostenPosition, KP_P.Personalausgaben_Kreativwirtschaft.path),
			Leistungsgruppe: get(kostenPosition, `${KP_P.Personalausgaben_Kreativwirtschaft.schema.attributes.Leistungsgruppe.path}.id`, {}),
		},
	};
	unset(Personalausgaben_Kreativwirtschaft, 'Personalausgaben_Kreativwirtschaft.id');

	const Sachausgaben_Kreativwirtschaft: any = {
		Sachausgaben_Kreativwirtschaft: {
			...get(kostenPosition, KP_P.Sachausgaben_Kreativwirtschaft.path),
		},
	};
	unset(Sachausgaben_Kreativwirtschaft, 'Sachausgaben_Kreativwirtschaft.id');
	return { ...Personalausgaben_Kreativwirtschaft, ...Sachausgaben_Kreativwirtschaft };
}

function getZeitplanung(antragsTyp: string, originalSkizze: any) {
	const antragsformular = get(originalSkizze, P.Allgemein_Antragsformular.path, null);
	switch (antragsformular) {
		case ANTRAGS_FORMULAREN.Loewe:
			const zeitplanungSkizze = get(originalSkizze, P.Zeitplanung_Zeitplanung_Block_1.path, []);
			if (zeitplanungSkizze.length === 0) {
				return [{ Zelle_6: 'Aufgabe' }];
			}
			return zeitplanungSkizze;
		default:
			const result =
				antragsTyp === ANTRAG_TYP.Skizze ?
					[{ Zelle_1: '' }]
				:	removeIdsFromArrayObjects(get(originalSkizze, P.Zeitplanung_Zeitplanung_Block_1.path, []));
			return result;
	}
}
/**
 * ************************************************************************************************************************************************************
 * ************************************************************ Helfer Funktion *******************************************************************************
 * *********************************************** Zentrale Funktion zum Filtern leerer Objekte ***************************************************************
 * ************************************************************************************************************************************************************
 */
function filterEmptyObjects(obj: any): any {
	if (!obj || typeof obj !== 'object') {
		return obj;
	}

	if (Array.isArray(obj)) {
		return obj.map((item) => filterEmptyObjects(item)).filter((item) => item !== null);
	}

	const filtered: any = {};
	let hasContent = false;

	for (const [key, value] of Object.entries(obj)) {
		if (value !== null && value !== undefined) {
			if (typeof value === 'object') {
				if (isObjectEmpty(value)) {
					continue; // Überspringe leere Objekte
				}
				const filteredValue = filterEmptyObjects(value);
				if (filteredValue !== null && !isObjectEmpty(filteredValue)) {
					filtered[key] = filteredValue;
					hasContent = true;
				}
			} else {
				filtered[key] = value;
				hasContent = true;
			}
		}
	}

	return hasContent ? filtered : null;
}
